import {useState, useEffect, useMemo} from 'react'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useEinstein from '_customHooks/use-einstein'
import {useLoader, useGlobalConfigState} from '_context'
import {BasketModel, OrderAddressModel, PaymentInstrumentModel} from '_types'
import {useWishlistActions} from '_customHooks/wishlist'
import {useMinicart, useCurrentBasket} from '_customHooks'

interface AddItemToBasketProps {
    productId: string
    quantity: number
    // einstein addToCart event props
    price?: number
}

/**
 * Shape returned by refetchBasket (useCurrentBasket's refetch, i.e. React Query's refetch).
 *
 * NOTE: `data` is the RAW getCustomerBaskets payload ({baskets, total}), NOT the single
 * currentBasket. useCurrentBasket derives currentBasket from `baskets[0]`, but refetch resolves
 * to the untransformed query result, so consumers must read `data.baskets[0]`. The previous
 * `as unknown as () => Promise<BasketModel>` cast was misleading and hid this.
 */
interface RefetchBasketResult {
    isSuccess: boolean
    data?: {
        baskets?: Array<BasketModel>
        total?: number
    }
}

type RefetchBasket = () => Promise<RefetchBasketResult>

interface addTaxesForBasketProps {
    [key: string]: {
        taxItems: {
            id: string
            rate: number
            value: number
        }[]
    }
}

interface UpdateBasketProps {
    currency?: string
    c_PP_API_TransactionID?: string
    c_alreadyConfirmed?: boolean
    c_is_guest?: boolean
    c_paypalPaymentMethod?: string
    c_siteId?: string
}

interface UpdateBasketCustomerProps {
    email: string
}

/**
 * @name useBasket
 *
 * @hook
 *
 * @description
 * Custom hook to manage basket.
 *
 * @remarks
 * When adding new basket mutations to this hook, always include the `c_locale` query parameter
 * in the mutation's parameters object. This is required to ensure that the mutation and
 * the basket query from useCurrentBasket share the same cache key.
 * If `c_locale` is omitted from a mutation, the cache will not update correctly and
 * components using the basket may show stale basket data after a mutation.
 *
 * @param {boolean} shouldShowLoader - Indicates if loader must be shown or not.
 *
 * @returns An object containing basket data and functions to manage basket items.
 */
const useBasket = (shouldShowLoader: boolean = true) => {
    const {locale} = useMultiSite()
    const {previewMinicart} = useMinicart()
    const einstein = useEinstein()
    const {appPreferences} = useGlobalConfigState()
    const {CountriesWithZeroPrices} = appPreferences

    const aliasCountry = useMemo(() => locale.alias?.split('-')[1].toLowerCase(), [locale])
    const hasHiddenPrices = useMemo(
        () => (Array.isArray(CountriesWithZeroPrices) ? CountriesWithZeroPrices.find((country: string) => country.toLowerCase() === aliasCountry) : undefined),
        [CountriesWithZeroPrices, aliasCountry],
    )

    const {data: basket, refetch: refetchBasket, isLoading: isLoadingBasket, isFetched: isBasketFetched} = useCurrentBasket()

    const {removeItemFromWishlist} = useWishlistActions()

    const {mutateAsync: createBasketMutation} = useShopperBasketsMutation('createBasket')
    const {mutateAsync: updateItemInBasketMutation} = useShopperBasketsMutation('updateItemInBasket')
    const {mutateAsync: removeItemFromBasketMutation} = useShopperBasketsMutation('removeItemFromBasket')
    const {mutateAsync: addItemToBasketMutation} = useShopperBasketsMutation('addItemToBasket')
    const {mutateAsync: updateBasketMutation} = useShopperBasketsMutation('updateBasket')
    const {mutateAsync: addTaxesForBasketMutation} = useShopperBasketsMutation('addTaxesForBasket')
    const {mutateAsync: updateShippingAddressMutation} = useShopperBasketsMutation('updateShippingAddressForShipment')
    const {mutateAsync: updateShippingMethodMutation} = useShopperBasketsMutation('updateShippingMethodForShipment')
    const {mutateAsync: addPaymentInstrumentMutation} = useShopperBasketsMutation('addPaymentInstrumentToBasket')
    const {mutateAsync: updateCustomerMutation} = useShopperBasketsMutation('updateCustomerForBasket')
    const {mutateAsync: removePaymentInstrumentFromBasket} = useShopperBasketsMutation('removePaymentInstrumentFromBasket')
    // see @remarks when adding new basket mutations

    const [basketProductsCount, setBasketProductsCount] = useState<number>(0)
    const {showLoader, hideLoader} = useLoader()

    /**
     * Resolves the basketId to use for a basket-item mutation, creating the basket lazily
     * only as a last resort (Salesforce standard behaviour: the basket is created on the
     * first addItemToBasket, not eagerly on mount).
     *
     * Order of resolution:
     *   1. Use the current basket's id when it is already loaded.
     *   2. If the customer baskets query has NOT fetched yet, refetch it first and read the
     *      inherited basket id from the raw payload ({baskets, total}). This preserves a basket
     *      that was created in another web app on the same browser/session (cross-app continuity):
     *      the shared SLAS cookies tie the basket to the same customer, so we must never ignore it.
     *   3. Only when there is still no basket do we create one, keeping c_locale so the create
     *      shares the same cache key as useCurrentBasket (per the hook @remarks).
     *
     * The refetch is guarded by isBasketFetched: after the first successful fetch we trust
     * `basket`. The query itself is gated on a ready customerId inside useCurrentBasket, so an
     * add (always a user action that happens after load) never triggers a refetch with
     * incomplete params.
     */
    const resolveBasketId = async (): Promise<string> => {
        let basketId = basket?.basketId

        if (!basketId && !isBasketFetched) {
            const result = (await refetchBasket()) as RefetchBasketResult
            basketId = result?.data?.baskets?.[0]?.basketId
        }

        if (!basketId) {
            const created = await createBasketMutation({
                parameters: {c_locale: locale?.id},
                body: {},
            })
            basketId = created?.basketId
            if (!basketId) {
                throw new Error('Basket creation failed: missing basketId')
            }
        }

        return basketId
    }

    const updateItemInBasket = async (basketItemId: string, quantity: number) => {
        if (shouldShowLoader) showLoader()

        try {
            await updateItemInBasketMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    itemId: basketItemId,
                    c_locale: locale?.id,
                },
                body: {
                    quantity: quantity,
                },
            })
        } catch (error) {
            console.error(error)
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const removeItemFromBasket = async (basketItemId: string) => {
        if (shouldShowLoader) showLoader()

        try {
            await removeItemFromBasketMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    itemId: basketItemId,
                    c_locale: locale?.id,
                },
            })
        } catch (error) {
            console.error(error)
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const addItemToBasket = async (products: Array<AddItemToBasketProps>, openMinicart = true) => {
        if (shouldShowLoader) showLoader()

        try {
            if (!hasHiddenPrices) {
                const basketId = await resolveBasketId()
                await addItemToBasketMutation({
                    parameters: {
                        basketId,
                        c_locale: locale?.id,
                    },
                    body: products.map(({productId, quantity}) => ({
                        productId,
                        quantity,
                    })),
                })

                einstein.sendAddToCart(
                    products.map(({productId, quantity, price}) => ({
                        productId,
                        quantity,
                        price,
                    })),
                )
            }
        } catch (error) {
            console.error(error)
        } finally {
            if (shouldShowLoader) hideLoader()
            if (openMinicart) previewMinicart()
        }
    }

    const updateBasket = async (basketUpdates: UpdateBasketProps) => {
        if (shouldShowLoader) showLoader()

        try {
            await updateBasketMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    c_locale: locale?.id,
                },
                body: basketUpdates,
            })
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const addTaxesForBasket = async (taxes: addTaxesForBasketProps) => {
        await addTaxesForBasketMutation({
            parameters: {
                basketId: basket?.basketId || '',
                c_locale: locale?.id,
            },
            body: {
                taxes: taxes,
            },
        })
    }

    const updateBasketShippingAddress = async (shippingAddress: OrderAddressModel, useAsBilling: boolean): Promise<BasketModel> => {
        if (shouldShowLoader) showLoader()

        try {
            const response = await updateShippingAddressMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    shipmentId: 'me',
                    useAsBilling,
                    c_locale: locale?.id,
                },
                body: shippingAddress || {},
            })
            return response as BasketModel
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const updateBasketShippingMethod = async (shippingMethodId: string) => {
        if (shouldShowLoader) showLoader()

        try {
            await updateShippingMethodMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    shipmentId: 'me',
                    c_locale: locale?.id,
                },
                body: {
                    id: shippingMethodId,
                },
            })
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const addPaymentInstrumentToBasket = async (paymentInstrument: PaymentInstrumentModel) => {
        if (shouldShowLoader) showLoader()

        try {
            await addPaymentInstrumentMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    c_locale: locale?.id,
                },
                body: paymentInstrument || {},
            })
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const updateBasketCustomer = async (customerUpdates: UpdateBasketCustomerProps) => {
        if (shouldShowLoader) showLoader()

        try {
            await updateCustomerMutation({
                parameters: {
                    basketId: basket?.basketId || '',
                    c_locale: locale?.id,
                },
                body: customerUpdates,
            })
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    const removePaymentInstrument = async (paymentInstrumentId: string) => {
        if (shouldShowLoader) showLoader()

        try {
            await removePaymentInstrumentFromBasket({
                parameters: {
                    basketId: basket?.basketId || '',
                    paymentInstrumentId: paymentInstrumentId,
                    c_locale: locale?.id,
                },
            })
        } catch (error) {
            console.error(error)
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    /**
     * Handles the buy products button behaviour in wishlist page.
     *
     * @description
     * All the products that have a selected size are moved into the basket and removed from the wishlist.
     *
     * @remarks
     * Variant ID has the priority over product ID for handle all buy products business logic.
     *
     * @param {Array<{productId: string, quantity: number, variantId?: string}>} productItems
     * @returns {Promise<void>} A promise that resolves when action is completed.
     */
    const addAllWishlistProductsToCart = async (
        productItems: Array<{productId: string; quantity: number; variantId?: string; price?: number}>,
    ): Promise<void> => {
        async function removeItemsSequentially(productItems: Array<{productId: string}>) {
            await productItems.reduce(async (previousPromise, item) => {
                await previousPromise
                return removeItemFromWishlist(item.productId)
            }, Promise.resolve())
        }

        try {
            if (shouldShowLoader) showLoader()

            await addItemToBasket(
                productItems.map((item) => ({
                    productId: item.variantId || item.productId,
                    quantity: item.quantity,
                    price: item.price,
                })),
            )

            await removeItemsSequentially(productItems)
        } catch (error) {
            console.error(error)
        } finally {
            if (shouldShowLoader) hideLoader()
        }
    }

    useEffect(() => {
        // Keep the basket currency aligned with the locale. The basket is NOT created eagerly here
        // anymore: it is created lazily on the first addItemToBasket (see resolveBasketId). This
        // currency sync only runs when a basket already exists.
        if (basket && !isLoadingBasket && locale && basket.currency !== locale.preferredCurrency) {
            void updateBasket({currency: locale.preferredCurrency})
        }
    }, [isLoadingBasket])

    useEffect(() => {
        if (basket) {
            setBasketProductsCount(basket?.c_productsQuantity || 0)
        }
    }, [basket])

    return {
        basket,
        removeItemFromBasket,
        updateItemInBasket,
        addItemToBasket,
        refetchBasket: refetchBasket as unknown as RefetchBasket,
        basketProductsCount,
        updateBasket,
        addTaxesForBasket,
        updateBasketShippingAddress,
        updateBasketShippingMethod,
        addPaymentInstrumentToBasket,
        updateBasketCustomer,
        addAllWishlistProductsToCart,
        removePaymentInstrument,
    }
}

export {useBasket}
